import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import type { CampaignWebsite } from '@/api/campaigns'
import type { ConnectorHealthReport, WebsiteHealth, WebsiteHealthStatus } from '@/api/types'
import { Badge, Card, EmptyState, formatInteger, formatRelativeTime, LoadingBlock, type BadgeTone } from '@/design'
import { ApiError } from '@/lib/api'

import { websiteLabel } from '../useIntegrationWebsites'
import { websiteHealthKeys } from './queryKeys'
import { utcLabel } from './utcLabel'
import './websites.css'

/** How often an open Integrations page asks again: the API reuses its event figures for a minute. */
const REFRESH_MS = 60_000

const STATUS: Record<WebsiteHealthStatus, { label: string; tone: BadgeTone; description: string }> = {
  connected: {
    label: 'Connected',
    tone: 'positive',
    description: 'Events are arriving and the connector reports no problems.',
  },
  no_events_yet: {
    label: 'No events yet',
    tone: 'neutral',
    description: 'No events received from this website in the last 7 days.',
  },
  stale: {
    label: 'Stale',
    tone: 'warning',
    description: 'No event has arrived from this website in the last 24 hours.',
  },
  needs_attention: {
    label: 'Needs attention',
    tone: 'critical',
    description: 'Events are being quarantined, or the connector reports a failed or missed reconcile run.',
  },
}

/**
 * One card per website of the tenant, in every environment: is its connector
 * delivering? Sits above the provider cards on the Integrations page.
 */
export function WebsiteHealthCards() {
  const client = useApiClient()
  const websites = useQuery({
    queryKey: websiteHealthKeys.websites(client.kind),
    queryFn: () => client.connectorWebsites(),
  })

  return (
    <section className="ci-website-health" aria-labelledby="ci-website-health-title">
      <h2 id="ci-website-health-title" className="ci-website-health__title">
        Website connectors
      </h2>
      <p className="ci-integrations__description">
        Whether each website&apos;s TmT connector is delivering: the newest event received, events quarantined, and
        the connector&apos;s own report of its last reconcile run.
      </p>
      {websites.isPending ? <LoadingBlock rows={3} /> : null}
      {websites.isError ? <HealthError error={websites.error} what="Websites" /> : null}
      {websites.data?.length === 0 ? (
        <EmptyState
          variant="no-data"
          compact
          title="No websites yet"
          description="A website appears here once TmT has registered it for this workspace."
        />
      ) : null}
      {websites.data && websites.data.length > 0 ? (
        <ul className="ci-website-health__list" aria-label="Website connectors">
          {websites.data.map((website) => (
            <li key={website.id}>
              <WebsiteHealthCard website={website} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function HealthError({ error, what }: { error: unknown; what: string }) {
  const refused = error instanceof ApiError && error.status === 403
  return (
    <EmptyState
      variant={refused ? 'permission' : 'outage'}
      compact
      description={
        refused
          ? 'Your role cannot read connector health. An Owner or Administrator can.'
          : `${what} could not be loaded. Try again shortly.`
      }
    />
  )
}

function WebsiteHealthCard({ website }: { website: CampaignWebsite }) {
  const client = useApiClient()
  const health = useQuery({
    queryKey: websiteHealthKeys.health(website.id, client.kind),
    queryFn: () => client.websiteHealth(website.id),
    refetchInterval: REFRESH_MS,
  })
  const status = health.data ? STATUS[health.data.status] : null

  return (
    <Card
      className="ci-website-health__card"
      title={websiteLabel(website)}
      headingLevel={3}
      busy={health.isFetching}
      actions={
        status ? (
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
        ) : null
      }
    >
      {health.isPending ? <LoadingBlock rows={3} /> : null}
      {health.isError ? <HealthError error={health.error} what="This website's health" /> : null}
      {health.data && status ? <HealthFacts health={health.data} description={status.description} /> : null}
    </Card>
  )
}

function HealthFacts({ health, description }: { health: WebsiteHealth; description: string }) {
  // Relative to when the figures were read rather than the wall clock: the two
  // differ by at most a minute, and the card then says the same thing wherever it renders.
  const now = new Date(health.computedAt)
  const { pageViews, commerce, entities } = health.lastEventAt
  return (
    <>
      <p className="ci-website-health__summary">{description}</p>
      <dl className="ci-website-health__facts">
        <Fact term="Page views" value={lastSeen(pageViews, now)} />
        <Fact term="Commerce" value={lastSeen(commerce, now)} />
        {/* The API's `entities` group: the purchases and refunds an order sync writes. */}
        <Fact term="Orders synced" value={lastSeen(entities, now)} />
        <Fact term="Quarantined (24 h)" value={formatInteger(health.quarantinedLast24h)} />
        <ReportFacts report={health.report} now={now} />
      </dl>
    </>
  )
}

function Fact({ term, value }: { term: string; value: ReactNode }) {
  return (
    <div className="ci-website-health__fact">
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function lastSeen(iso: string | null, now: Date): ReactNode {
  if (!iso) return <span className="ci-website-health__none">None in 7 days</span>
  return <time dateTime={iso} title={utcLabel(iso)}>{formatRelativeTime(iso, now)}</time>
}

function ReportFacts({ report, now }: { report: ConnectorHealthReport | null; now: Date }) {
  if (!report) {
    return <Fact term="Connector" value={<span className="ci-website-health__none">No health report yet</span>} />
  }
  return (
    <>
      <Fact term="Last reconcile" value={<LastRun report={report} />} />
      <Fact
        term="Next run"
        value={
          report.missedRun ? (
            <Badge tone="critical">Run missed</Badge>
          ) : report.nextRunDueAt ? (
            <time dateTime={report.nextRunDueAt}>{utcLabel(report.nextRunDueAt)}</time>
          ) : (
            'Not scheduled'
          )
        }
      />
      <Fact term="Retry queue" value={queueSummary(report)} />
      <Fact
        term="Connector"
        value={`v${report.connectorVersion} · contract ${report.contractVersion}`}
      />
      <Fact
        term="Last report"
        value={<time dateTime={report.reportedAt} title={utcLabel(report.reportedAt)}>{formatRelativeTime(report.reportedAt, now)}</time>}
      />
    </>
  )
}

function LastRun({ report }: { report: ConnectorHealthReport }) {
  if (!report.lastReconcileWindowFrom || !report.lastReconcileWindowTo) {
    return <span className="ci-website-health__none">No run completed yet</span>
  }
  const failed = report.lastReconcileFailed
  return (
    <span className="ci-website-health__run">
      <span>
        {utcLabel(report.lastReconcileWindowFrom)} to {utcLabel(report.lastReconcileWindowTo)}
      </span>
      <span>
        {formatInteger(report.lastReconcileSent)} sent · {formatInteger(report.lastReconcileSkipped)} skipped ·{' '}
        <span className={failed > 0 ? 'ci-website-health__failed' : undefined}>{formatInteger(failed)} failed</span>
        {failed > 0 && report.lastReconcileErrorClass ? ` (${report.lastReconcileErrorClass})` : null}
      </span>
    </span>
  )
}

function queueSummary(report: ConnectorHealthReport): string {
  const waiting = `${formatInteger(report.queueBacklog)} waiting · ${formatInteger(report.queueParked)} parked`
  const age = report.queueOldestAgeSeconds
  return age === null ? waiting : `${waiting} · oldest ${formatAge(age)}`
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return hours < 24 ? `${hours} h` : `${Math.floor(hours / 24)} d`
}
